/* ============================================================
   Trasse Families — school trips your child is on.

   The visible payoff of a school link. Everything else about linking happens
   in Settings and then goes quiet; this is the panel that makes a household
   feel it bought something — the school added their child to the match on
   Saturday and nobody had to chase an invite code.

   The rows are a DERIVED view (`familyTripParticipations`) written by the
   backend when a school enrols the child. The household can read them because
   each row carries the FAMILY's orgCode; it can never write one, which is why
   a school cannot be quietly added to by the family it is carrying.

   Renders nothing at all when there are no trips. A household with no school
   link should not see an empty box explaining a feature it is not using.
   ============================================================ */
import React, { useEffect, useState } from 'react';
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { FAM, DISPLAY_FONT } from './familyTheme';

interface Participation {
  id: string;
  tripName: string;
  childName: string;
  schoolCode: string;
  tripDate: Date | null;
}

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') return (v as { toDate: () => Date }).toDate();
  if (v instanceof Date) return v;
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function fmt(d: Date | null): string {
  if (!d) return 'Date to be confirmed';
  return d.toLocaleDateString('en-KE', { weekday: 'long', day: 'numeric', month: 'long' });
}

interface Props { orgCode: string }

export default function FamilySchoolTripsPanel({ orgCode }: Props) {
  const [trips, setTrips] = useState<Participation[]>([]);

  /* Sorted HERE, not by Firestore. `where(orgCode) + orderBy(addedAt)` is a
     composite index, and there is no familyTripParticipations entry in
     firestore.indexes.json — so the query failed FAILED_PRECONDITION, the
     error handler set [], and the panel rendered null forever: a school could
     add a child to a trip and the household's website never showed one
     (review, 09-06). A household has a handful of rows; ordering twenty of
     them in the browser costs nothing and needs no deploy. The mobile twin
     (`_SchoolTrips` in today_screen.dart) already does exactly this. */
  useEffect(() => {
    if (!orgCode) return;
    const unsub = onSnapshot(
      query(
        collection(db, 'familyTripParticipations'),
        where('orgCode', '==', orgCode),
        limit(20),
      ),
      (snap) => {
        const rows: (Participation & { addedAt: Date | null })[] = [];
        snap.forEach((d) => {
          const x = d.data() as Record<string, unknown>;
          rows.push({
            id: d.id,
            tripName: String(x.tripName || 'School trip'),
            childName: String(x.childName || 'Your child'),
            schoolCode: String(x.schoolCode || ''),
            tripDate: toDate(x.tripDate),
            addedAt: toDate(x.addedAt),
          });
        });
        rows.sort((a, b) => (b.addedAt?.getTime() ?? 0) - (a.addedAt?.getTime() ?? 0));
        setTrips(rows);
      },
      () => setTrips([]),
    );
    return () => unsub();
  }, [orgCode]);

  if (trips.length === 0) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcoming = trips.filter((t) => !t.tripDate || t.tripDate >= today);
  const past = trips.filter((t) => t.tripDate && t.tripDate < today);

  return (
    <section style={{
      background: '#fff', border: `1px solid ${FAM.line}`, borderRadius: 20,
      padding: 'clamp(18px,3vw,26px)',
    }}>
      <h2 style={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 30, letterSpacing: '-.01em', margin: 0 }}>
        School trips
      </h2>
      <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: FAM.ink2, maxWidth: 560 }}>
        Trips the school has put your child on. These run on the school&apos;s bus with the
        school&apos;s driver — your own car and driver are not involved.
      </p>

      {upcoming.length > 0 && (
        <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 9 }}>
          {upcoming.map((t) => (
            <div key={t.id} style={{
              padding: '13px 16px', borderRadius: 14,
              background: FAM.tint, border: `1px solid ${FAM.tint2}`,
            }}>
              <div style={{ fontSize: 15.5, fontWeight: 700, color: FAM.ink }}>{t.tripName}</div>
              <div style={{ marginTop: 3, fontSize: 13.5, color: FAM.ink2 }}>
                {t.childName} · {fmt(t.tripDate)}
              </div>
            </div>
          ))}
        </div>
      )}

      {past.length > 0 && (
        <div style={{ marginTop: upcoming.length ? 16 : 18 }}>
          <div style={{
            fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em',
            textTransform: 'uppercase', color: FAM.muted, marginBottom: 8,
          }}>Been on</div>
          {past.slice(0, 5).map((t) => (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap',
              padding: '9px 13px', borderRadius: 10, border: `1px solid ${FAM.line}`, marginBottom: 6,
            }}>
              <span style={{ fontSize: 14, color: FAM.ink2, flex: '1 1 auto', minWidth: 0 }}>
                {t.tripName}
              </span>
              <span style={{ fontSize: 12.5, color: FAM.muted }}>{fmt(t.tripDate)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
